package one.healthium.tutorial.screens

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEach
import kotlinx.coroutines.launch
import loco.GENERAL_CONTINUE
import one.healthium.onboarding.ui.components.HighlightItem
import one.healthium.onboarding.ui.user_binding.UserProvider
import one.healthium.tutorial.domain.TutorialScreenConfig
import one.healthium.ui.common.utils.toColor
import one.healthium.ui.common.utils.toTextStyle
import one.healthium.ui.compose.components.AppButton
import one.healthium.ui.compose.components.AppText
import one.healthium.ui.compose.theme.AppTheme
import one.primehealth.analytics.permissions.TrackingPermission
import org.jetbrains.compose.resources.stringResource

interface TutorialTransparencyAccessEventDelegate {
    fun onNextClicked()
}

@Composable
fun TutorialTransparencyAccessScreen(
    screenConfig: TutorialScreenConfig.TutorialTransparencyAccessScreenConfig,
    eventDelegate: TutorialTransparencyAccessEventDelegate,
) {
    TutorialTransparencyAccessScreenContent(
        screenConfig = screenConfig,
        onNextClicked = eventDelegate::onNextClicked,
    )
}

@Composable
private fun TutorialTransparencyAccessScreenContent(
    modifier: Modifier = Modifier,
    screenConfig: TutorialScreenConfig.TutorialTransparencyAccessScreenConfig,
    onNextClicked: () -> Unit,
) {
    val scope = rememberCoroutineScope()

    UserProvider {
        Column(
            modifier = modifier
                .navigationBarsPadding()
                .padding(horizontal = 24.dp)
                .fillMaxHeight()
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Spacer(Modifier.height(36.dp))
            AppText(
                modifier = Modifier.padding(horizontal = 8.dp),
                text = screenConfig.title?.bindUserPlaceholders(),
                style = screenConfig.titleStyle.toTextStyle(AppTheme.textStyles.secondary_30_500),
                color = screenConfig.titleStyle?.color?.toColor() ?: AppTheme.colors.text100,
                textAlign = TextAlign.Center,
            )
            Spacer(Modifier.height(16.dp))
            AppText(
                modifier = Modifier.padding(horizontal = 8.dp),
                text = screenConfig.subtitle?.bindUserPlaceholders(),
                style = AppTheme.textStyles.secondary_18_400,
                color = AppTheme.colors.text500,
                textAlign = TextAlign.Center,
            )
            Spacer(Modifier.height(24.dp))
            Spacer(Modifier.weight(0.5f))
            AppText(
                modifier = Modifier.fillMaxWidth(),
                text = screenConfig.benefitsTitle?.bindUserPlaceholders(),
                style = screenConfig.benefitsTitleLabel.toTextStyle(AppTheme.textStyles.poppins22SemiBold),
            )
            Spacer(Modifier.height(5.dp))
            screenConfig.items.fastForEach { highlightItem ->
                HighlightItem(
                    text = highlightItem.text.bindUserPlaceholders(),
                    emoji = highlightItem.emoji.bindGenderValue(),
                    emojiHeight = screenConfig.emojiHeight ?: 40,
                    textStyle = screenConfig.textLabel,
                    checkmarkTint = AppTheme.colors.primary,
                )
            }
            Spacer(Modifier.height(32.dp))
            Spacer(Modifier.weight(0.6f))
            AppButton(
                modifier = Modifier.defaultMinSize(minWidth = 176.dp),
                onClick = {
                    scope.launch {
                        TrackingPermission.requestTrackingAuthorization()
                        onNextClicked()
                    }
                },
            ) {
                Text(text = screenConfig.ctaTitle ?: stringResource(loco.Res.string.GENERAL_CONTINUE))
            }
            Spacer(Modifier.height(50.dp))
        }
    }
}